'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Reveal — a small, tasteful scroll-reveal wrapper for the storefront
 * *chrome* sections (hero / gallery / contact / hours / …). NOT for the
 * booking wizard, which stays snappy and quiet.
 *
 * Behaviour:
 *  - On first enter-viewport: opacity 0 → 1 + translateY(offsetY → 0) over
 *    ~450–600ms ease-out, exactly ONCE (observer disconnects after).
 *  - `prefers-reduced-motion`: no transform, content shown instantly.
 *  - Fail-open + SSR-safe: the hidden start-state is only adopted when the
 *    client has both `IntersectionObserver` and motion enabled. If JS never
 *    runs, if IO is unavailable, or if motion is reduced, the content renders
 *    visible — it is NEVER left permanently invisible.
 *  - RTL-safe: only the vertical axis is animated (translateY), no
 *    directional (x) offset.
 *
 * The wrapper is a plain <div>; pass `className` (e.g. `h-full`, grid-item
 * classes) so it can slot in wherever the original element sat.
 */
interface RevealProps {
  children: ReactNode;
  /** Stagger delay in ms — offset siblings slightly for a refined cascade. */
  delay?: number;
  /** Animation duration in ms (kept in the 450–600ms "calm" band). */
  durationMs?: number;
  /** Initial downward offset in px (animates to 0). */
  offsetY?: number;
  className?: string;
}

function shouldAnimate(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof IntersectionObserver === 'undefined') return false;
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return false;
  }
  return true;
}

export function Reveal({
  children,
  delay = 0,
  durationMs = 520,
  offsetY = 14,
  className = '',
}: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Lazy initializer runs on the client at mount (the storefront chrome is
  // only ever rendered after the client-side data fetch, so there is no SSR
  // output for this subtree → no hydration mismatch). Default to shown =
  // fail-open; only start hidden when we can actually animate.
  const [shown, setShown] = useState<boolean>(() => !shouldAnimate());

  useEffect(() => {
    if (shown) return; // reduced-motion / no-IO → already visible, nothing to do.
    const el = ref.current;
    if (!el) return;

    let io: IntersectionObserver | null = null;
    // Fail-open safety net: if the observer never fires (e.g. an element that
    // is display:none, a buggy IO impl, or a callback that errors out), force
    // the content visible after a short grace period so it is NEVER left at
    // opacity:0 forever.
    const failOpen = window.setTimeout(() => {
      try {
        io?.disconnect();
      } catch {
        /* ignore */
      }
      setShown(true);
    }, 1200);

    try {
      io = new IntersectionObserver(
        (entries) => {
          try {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                setShown(true);
                io?.disconnect();
                window.clearTimeout(failOpen);
                break;
              }
            }
          } catch {
            // Any callback error → fail open immediately.
            setShown(true);
            window.clearTimeout(failOpen);
          }
        },
        // Trigger a touch before fully in view for a natural feel.
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
      );
      io.observe(el);
    } catch {
      // Observer creation or observe() threw → fail open immediately.
      window.clearTimeout(failOpen);
      setShown(true);
    }

    return () => {
      window.clearTimeout(failOpen);
      try {
        io?.disconnect();
      } catch {
        /* ignore */
      }
    };
    // Mount-only: `shown` starts false in the animate path and we bail early
    // otherwise; re-running on `shown` would only fire the no-op guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : `translateY(${offsetY}px)`,
        transition: `opacity ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: shown ? undefined : 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
}
